import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import firebase from '../firebaseConfig';
import { shareLinkErrorMessage } from '../shareTarget';
import { routeMemberHome } from '../shareRouting';
import PasswordGate from '../components/PasswordGate';
import SharedPlayer from '../components/SharedPlayer';
import OpenInAppBanner from '../components/OpenInAppBanner';
import BrandBar from '../components/BrandBar';
import Login from '../components/Login';
import GradientThumb from '../components/GradientThumb';
import Helpers from '../Helpers';
import { track as trackEvent } from '../analytics';
import icon from '../icon.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faPause, faSpinner } from '@fortawesome/free-solid-svg-icons';

/**
 * /p/:slug — global project share link viewer. Reads everything via the
 * `redeemProjectLink` Cloud Function (no direct RTDB reads, since the visitor
 * may be anonymous and /artists/* isn't publicly readable).
 *
 * States, in order of priority:
 *   loading   → fetching the redeem
 *   error     → unrecoverable (revoked/expired/missing)
 *   accountReq→ requireAccount=true and visitor not signed in
 *   pwGate    → password-required (could be wrong-password retry too)
 *   ready     → render project + tracks + player
 */
function PublicProject() {
    const { slug } = useParams();
    const navigate = useNavigate();

    const [status, setStatus] = useState('loading'); // loading | error | accountReq | pwGate | ready
    const [errorMessage, setErrorMessage] = useState(null);
    const [pwError, setPwError] = useState(null);
    const [data, setData] = useState(null); // { project, artistId, projectId, permission, allowDownloads, requireAccount, requiresPassword, isArtistMember }
    const [password, setPassword] = useState(null);
    const [albumArtUrl, setAlbumArtUrl] = useState(null);
    const [currentTrack, setCurrentTrack] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const playerRef = useRef(null);

    const callRedeem = async (pwd) => {
        const fn = firebase.functions().httpsCallable('redeemProjectLink');
        const res = await fn({ slug, password: pwd || undefined });
        return res.data;
    };

    const routeAfterRedeem = (result) => {
        if (routeMemberHome(result, navigate)) return;
        if (firebase.auth().currentUser) {
            navigate(`/home?shared=${encodeURIComponent(slug)}`, { replace: true });
            return;
        }
        setData(result);
        setStatus('ready');
    };

    // Redeem inside onAuthStateChanged so the CF call carries the ID token —
    // otherwise isArtistMember is always false on cold start while persisted
    // auth restores from IndexedDB.
    useEffect(() => {
        trackEvent('share_link_opened', { slug, kind: 'project' });
        let cancelled = false;
        let fired = false;
        const unsub = firebase.auth().onAuthStateChanged(async () => {
            if (fired || cancelled) return;
            fired = true;
            try {
                const result = await callRedeem(null);
                if (cancelled) return;
                trackEvent('share_link_redeemed', {
                    slug,
                    kind: 'project',
                    permission: result?.permission || 'unknown',
                });
                routeAfterRedeem(result);
            } catch (e) {
                if (cancelled) return;
                handleRedeemError(e);
            }
        });
        return () => { cancelled = true; unsub(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    const handleRedeemError = (e) => {
        const reason = e?.details?.reason;
        if (reason === 'password-required') {
            setStatus('pwGate');
            return;
        }
        if (reason === 'account-required') {
            setStatus('accountReq');
            return;
        }
        setStatus('error');
        setErrorMessage(shareLinkErrorMessage(e));
    };

    const onPasswordSubmit = async (pwd) => {
        try {
            const result = await callRedeem(pwd);
            setPassword(pwd);
            setPwError(null);
            routeAfterRedeem(result);
        } catch (e) {
            const reason = e?.details?.reason;
            if (reason === 'password-required') {
                setPwError('Wrong password. Try again.');
                return;
            }
            handleRedeemError(e);
        }
    };

    // Album art URL is resolved by the redeem CF and returned in the
    // response payload. Keeps the redeem call as the single authoritative
    // gate (no separate RTDB read whose access depends on rule wording).
    useEffect(() => {
        if (!data) { setAlbumArtUrl(null); return; }
        setAlbumArtUrl(data.albumArtUrl || null);
    }, [data]);

    // Build the tracks list once data is in.
    const tracks = useMemo(() => {
        if (!data || !data.project || !data.project.tracks) return [];
        const entries = Object.entries(data.project.tracks);
        const list = entries.map(([id, track]) => {
            const meta = (track && track.metadata) || {};
            const currentRevId = meta['current revision'];
            const rev = currentRevId && track.revisions && track.revisions[currentRevId];
            const revMeta = (rev && rev.metadata) || {};
            return {
                id,
                name: meta.name || 'Untitled',
                duration: revMeta.duration || 0,
                orderWeight: meta['order weight'] || 0,
                isLocked: meta.locked === true,
            };
        });
        list.sort((a, b) => a.orderWeight - b.orderWeight);
        return list;
    }, [data]);

    // Audio fetcher for the SharedPlayer — calls getSharedTrackAudioURLBySlug.
    const audioFetcher = useMemo(() => {
        return async (trackId) => {
            const fn = firebase.functions()
                .httpsCallable('getSharedTrackAudioURLBySlug');
            const res = await fn({ slug, trackId, password: password || undefined });
            return res.data;
        };
    }, [slug, password]);

    const playLogger = useMemo(() => {
        return async (trackId, ms, sessionId) => {
            const fn = firebase.functions().httpsCallable('logSharedTrackPlay');
            await fn({ slug, trackId, ms, sessionId });
        };
    }, [slug]);

    // ------------------------------------------------------------------
    // Render
    // ------------------------------------------------------------------

    if (status === 'loading') {
        return (
            <div className="splash-screen">
                <img src={icon} alt="Demodeck" className="splash-logo" />
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div className="shared-error">
                <img src={icon} alt="Demodeck" className="shared-error-logo" />
                <div className="shared-error-message">{errorMessage}</div>
            </div>
        );
    }

    if (status === 'accountReq') {
        const onSignedIn = () => {
            // Re-attempt the redeem now that we're signed in.
            setStatus('loading');
            (async () => {
                try {
                    const result = await callRedeem(password);
                    routeAfterRedeem(result);
                } catch (e) {
                    handleRedeemError(e);
                }
            })();
        };
        return (
            <div className="shared-account-gate">
                <img src={icon} alt="Demodeck" className="shared-account-gate-logo" />
                <div className="shared-account-gate-title">Sign in to listen</div>
                <div className="shared-account-gate-subtitle">
                    The owner restricted this link to Demodeck users.
                </div>
                <Login onLoginSuccess={onSignedIn} embedded />
            </div>
        );
    }

    if (status === 'pwGate') {
        return <PasswordGate onSubmit={onPasswordSubmit} errorMessage={pwError} />;
    }

    // Ready
    const project = data.project;
    const projectName = (project?.metadata?.name) || 'Untitled';
    const artistName = data.artistName || null;
    const trackCount = tracks.length;
    const totalSec = tracks.reduce((acc, t) => acc + (t.duration || 0), 0);

    return (
        <>
            <BrandBar />
            <div className="shared-page">
                <OpenInAppBanner />
            <div className="shared-page-header">
                {albumArtUrl ? (
                    <img src={albumArtUrl} alt="album art" className="shared-page-art" />
                ) : (
                    <GradientThumb id={data.projectId} className="shared-page-art" />
                )}
                <div className="shared-page-title-row">
                    <div className="shared-page-meta">
                        <div className="shared-page-title">{projectName}</div>
                        {artistName && (
                            <div className="shared-page-artist">{artistName}</div>
                        )}
                        <div className="shared-page-subtitle">
                            {trackCount} track{trackCount === 1 ? '' : 's'} · {Helpers.formatDuration(totalSec)}
                        </div>
                    </div>
                    <button
                        className="shared-page-play"
                        onClick={() => {
                            if (!tracks.length) return;
                            if (currentTrack) playerRef.current?.togglePlay();
                            else setCurrentTrack(tracks[0]);
                        }}
                        disabled={!tracks.length || isLoading}
                        aria-label={isPlaying ? 'Pause project' : 'Play project'}
                    >
                        <FontAwesomeIcon
                            icon={isLoading ? faSpinner : (isPlaying ? faPause : faPlay)}
                            className={isLoading ? 'track-spin' : ''}
                        />
                    </button>
                </div>
            </div>

            <ol className="shared-page-tracklist">
                {tracks.map((track, i) => (
                    <li
                        key={track.id}
                        className={`shared-page-track${currentTrack?.id === track.id ? ' is-playing' : ''}${track.isLocked ? ' is-locked' : ''}`}
                        onClick={() => !track.isLocked && setCurrentTrack(track)}
                    >
                        <span className="shared-page-track-number">{i + 1}</span>
                        <span className="shared-page-track-name">{track.name}</span>
                        <span className="shared-page-track-duration">
                            {Helpers.formatDuration(track.duration || 0)}
                        </span>
                    </li>
                ))}
            </ol>

            <SharedPlayer
                ref={playerRef}
                tracks={tracks}
                currentTrack={currentTrack}
                setCurrentTrack={setCurrentTrack}
                audioFetcher={audioFetcher}
                playLogger={playLogger}
                projectId={data.projectId}
                albumArtUrl={albumArtUrl}
                projectName={projectName}
                artistName={artistName}
                onPlayingChange={setIsPlaying}
                onLoadingChange={setIsLoading}
            />
            </div>
        </>
    );
}

export default PublicProject;
